import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "src/api";
import { useMerchflowsApi } from "src/api/merchflows";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import { StoresSelector } from "src/components/StoresSelector/StoresSelector";
import type { StoresSelectorStore } from "src/components/StoresSelector/store/types";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  Grid,
  Input,
  Modal,
  Radios,
  Scroller,
  Tabs,
  Text,
} from "src/elements";
import { SearchUncontrolled } from "src/elements/SearchUncontrolled/SearchUncontrolled";
import { RoutePostMerchflows_merchflowId_Duplicate } from "@CommonApi/merchflows/_merchflowId_/duplicate";
import { useLoaders } from "src/components/InlineLoader";
import { duplicateMerchflowModalAtom } from "./store/atoms";

export const ModalDuplicateMerchflow = () => {
  const [data, setData] = useAtom(duplicateMerchflowModalAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  return data ? <ModalDuplicateMerchflowInner {...data} /> : null;
};

type PogVersionMode = "latest" | "all";
type StoresMode = "all" | "selected";
type StoreSelectionTab = "attribute" | "code";

interface Props {
  id: number;
  name: string;
  onSuccess?: () => void;
}

const ModalDuplicateMerchflowInner = ({ id, name, onSuccess }: Props) => {
  const { closeModal } = useModals();
  const { getMerchflowReviewApi } = useApi();
  const { duplicateMerchflowApi } = useMerchflowsApi();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();

  const [customName, setCustomName] = useState("");
  const [pogVersionMode, setPogVersionMode] = useState<PogVersionMode>("latest");
  const [storesMode, setStoresMode] = useState<StoresMode>("all");
  const [storeSelectionTab, setStoreSelectionTab] = useState<StoreSelectionTab>("attribute");
  const [availableStores, setAvailableStores] = useState<StoresSelectorStore[]>([]);
  const [selectedStores, setSelectedStores] = useState<StoresSelectorStore[]>([]);
  const [selectedStoreCodes, setSelectedStoreCodes] = useState<string[]>([]);
  const [storeSearch, setStoreSearch] = useState("");
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [refreshSales, setRefreshSales] = useState(false);

  const isSubmitting = hasLoader(RoutePostMerchflows_merchflowId_Duplicate);

  useEffect(() => {
    if (storesMode === "selected" && availableStores.length === 0) {
      void loadStores();
    }
  }, [storesMode]);

  const loadStores = async () => {
    setIsLoadingStores(true);
    const review = await getMerchflowReviewApi(id);
    if (review) {
      setAvailableStores(
        review.filter_config.stores.map((s) => ({
          store: s.store,
          state: s.state,
          cluster: s.cluster,
          bay_size: s.bay_size,
        })),
      );
    }
    setIsLoadingStores(false);
  };

  const filteredStoresBySearch = useMemo(
    () =>
      availableStores
        .filter((s) => s.store.toLowerCase().includes(storeSearch.toLowerCase()))
        .sort((a, b) => (a.store < b.store ? -1 : 1)),
    [availableStores, storeSearch],
  );

  const toggleStoreCode = (code: string) => {
    setSelectedStoreCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const selectAllStoreCodes = () => {
    setSelectedStoreCodes(filteredStoresBySearch.map((s) => s.store));
  };

  const clearStoreCodes = () => {
    setSelectedStoreCodes([]);
  };

  const close = () => {
    closeModal("ModalDuplicateMerchflow");
  };

  const getStoreCodes = (): string[] => {
    if (storeSelectionTab === "attribute") return selectedStores.map((s) => s.store);
    return selectedStoreCodes;
  };

  const handleSubmit = async () => {
    const result = await duplicateMerchflowApi(id, {
      name: customName.trim() || undefined,
      include_all_pog_versions: pogVersionMode === "all",
      store_codes: storesMode === "selected" ? getStoreCodes() : null,
      refresh_sales: refreshSales,
    });

    if (result) {
      if (onSuccess) onSuccess();
      close();

      if (result.skipped_count > 0) {
        toast({
          title: `${result.skipped_count} store(s) without a completed planogram weren't copied.`,
        });
      }

      if (result.sales_refresh && result.sales_refresh.unmatched_product_count > 0) {
        toast({
          title: `${result.sales_refresh.unmatched_product_count} product(s) had no current data and kept their previous movement and sales.`,
        });
      }
    }
  };

  const activeSelectionCount =
    storeSelectionTab === "attribute" ? selectedStores.length : selectedStoreCodes.length;
  const isSubmitDisabled = storesMode === "selected" && activeSelectionCount === 0;

  return (
    <Modal name="ModalDuplicateMerchflow" title={`Duplicate "${name}"`} width="600px">
      <Flex column padding="16px" gap="16px">
        <Input
          title="Name (optional)"
          value={customName}
          setValue={setCustomName}
          placeholder={`Copy of ${name}`}
        />

        <Radios
          label="POG version"
          direction="column"
          options={[
            { label: "Latest version only", value: "latest" as PogVersionMode },
            { label: "All versions", value: "all" as PogVersionMode },
          ]}
          value={pogVersionMode}
          setValue={setPogVersionMode}
        />

        <Radios
          label="Stores"
          direction="column"
          options={[
            { label: "All stores", value: "all" as StoresMode },
            { label: "Selected stores", value: "selected" as StoresMode },
          ]}
          value={storesMode}
          setValue={setStoresMode}
        />

        <Text variant="small2" color={Color.danger}>
          Stores without a completed planogram won't be included.
        </Text>

        <Flex column gap="4px">
          <Checkbox
            label="Recalculate movement & sales from latest product data"
            isChecked={refreshSales}
            setIsChecked={setRefreshSales}
            isCheckboxLeft
            role="checkbox"
          />
          <Text variant="small2">Planogram layout is not changed.</Text>
        </Flex>

        {storesMode === "selected" && (
          <Flex column gap="12px">
            <Tabs
              tabs={[
                { label: "By Attribute", value: "attribute" as StoreSelectionTab },
                { label: "By Store Code", value: "code" as StoreSelectionTab },
              ]}
              value={storeSelectionTab}
              setValue={setStoreSelectionTab}
            />

            {isLoadingStores ? (
              <Text variant="small2">Loading stores...</Text>
            ) : storeSelectionTab === "attribute" ? (
              <StoresSelector
                availableStores={availableStores}
                selectedStores={selectedStores}
                setSelectedStores={setSelectedStores}
              />
            ) : (
              <Flex column gap="8px">
                <Flex align="center" justify="between" gap="8px">
                  <SearchUncontrolled
                    placeholder="Search store code"
                    width="200px"
                    onSearch={setStoreSearch}
                  />
                  <Flex gap="8px">
                    <Button variant="inverted" onClick={selectAllStoreCodes}>
                      Select all
                    </Button>
                    <Button variant="inverted" onClick={clearStoreCodes}>
                      Clear
                    </Button>
                  </Flex>
                </Flex>

                <Scroller maxHeight="200px">
                  <Grid columns="repeat(7, 1fr)" gap="6px">
                    {filteredStoresBySearch.map((s) => {
                      const isSelected = selectedStoreCodes.includes(s.store);
                      return (
                        <Button
                          key={s.store}
                          variant={isSelected ? "default" : "inverted"}
                          onClick={() => toggleStoreCode(s.store)}
                        >
                          {s.store}
                        </Button>
                      );
                    })}
                  </Grid>
                </Scroller>

                {selectedStoreCodes.length > 0 && (
                  <Text variant="small2">{selectedStoreCodes.length} store(s) selected</Text>
                )}
              </Flex>
            )}
          </Flex>
        )}

        <Flex gap="10px">
          <Button color="primary" variant="inverted" width="50%" onClick={close}>
            Cancel
          </Button>

          <Button
            width="50%"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isEnabled={!isSubmitDisabled}
          >
            Duplicate
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
